/*
Lookup tables for the Calypso Startup Information returned by the
SELECT APPLICATION command, sourced from technical note
000907-TN-001-StartupInfo (docs/CalypsoTN001.md).

This SPA-side mirror lets card-detail pages render human-readable
names for already-stored cards whose Android-side decoded payload
predates the matching lookup helper on the device — old uploads only
carry the bare hex form "0x08" in the `value` field.

Three table shapes:
  - kPlatformTable / kIssuerTable: flat byte → name maps.
  - getApplicationTypeName: range-based dispatcher.

Every helper returns null when no entry matches; callers fall back
to whatever the server-supplied DecodedField.value already carries.
*/

const kPlatformTable: Readonly<Record<number, string>> =		// Platform code → name
{
	0x00: "Undefined",
	0x01: "ST16V623",
	0x02: "ST16601",
	0x03: "ST16RF42",
	0x04: "M35101",
	0x05: "RFU",
	0x06: "ST16SF4F",
	0x07: "ST16RF58",
	0x08: "ST16R820",
	0x09: "ST16VF52",
	0x0A: "ST16RF52",
	0x14: "ST16SF48",
	0x21: "ST19XR08",
	0x22: "ST19XR34",
	0x23: "ST19NM34",
	0x28: "ST19WR02",
	0x29: "ST19WR08",
	0x2A: "ST19NL66",
	0x2B: "ST23YR80",
	0x2C: "ST23YR18/08/04",
	0x2D: "ST23ZR08/04/02",
	0x2E: "ST31G Family",
	0x2F: "ST31N Family",
	0x30: "AT05SC4002RF",
	0x31: "SLE66CLX320P",
	0x32: "S3CC9G4",
	0x33: "SLE66CL80PE",
	0x34: "SLE66CLX800PE",
	0x36: "AT90SC6408RFT",
	0x37: "SLE66CL41PE",
	0x38: "S3CC9H4",
	0x39: "S3CC9L5",
	0x3A: "SLE77CLF1004PM",
	0x3B: "SLE77CLF1360P",
	0x3C: "SLE77CLFXxxxxP(M)",
	0x3D: "SLE77CLF136AP",
	0x3E: "SLE77CLFxyP(M)",
	0x3F: "SLC32 Family",
	0x41: "P8RF5016",
	0x42: "P5SD009",
	0x43: "E201382 EMBRACE",
	0x48: "ST33F Family",
	0x49: "ST33J Family",
	0x50: "SLC36 Family",
	0x51: "SLM10TLD002Y",
	0x52: "SLC26 Family",
	0xC0: "Java Card platform",
	0xD1: "nShield Solo F3 (HSM)",
	0xD2: "nShield Connect (HSM)",
	0xE0: "Calypso HCE container",
};

const kIssuerTable: Readonly<Record<number, string>> =		// Software issuer code → name
{
	0x00: "Paragon ID (ASK)",
	0x01: "Intec",
	0x02: "Calypso Networks Association",
	0x03: "Conduent (Xerox)",
	0x04: "Thales",
	0x05: "Idemia (Morpho / Sagem)",
	0x06: "Thales DIS (Axalto)",
	0x07: "Bull",
	0x08: "Spirtech",
	0x09: "BMS",
	0x0A: "Idemia (Oberthur Technologies)",
	0x0B: "Thales DIS (Gemplus)",
	0x0C: "Magnadata",
	0x0D: "Calmell",
	0x0E: "Mecstar",
	0x0F: "ACG Identification",
	0x10: "STMicroelectronics",
	0x11: "Calypso Networks Association",
	0x12: "Giesecke & Devrient",
	0x13: "OTI",
	0x14: "Thales DIS (Gemalto)",
	0x15: "Watchdata",
	0x16: "Alios",
	0x17: "S-P-S",
	0x18: "ISRA",
	0x19: "Trust Electronics",
	0x1A: "Trusted Labs",
	0x1B: "Neowave",
	0x1C: "Digital People",
	0x1D: "ABNote Europe",
	0x1E: "Twinlinx",
	0x1F: "Inteligensa",
	0x20: "Calypso Networks Association",
	0x21: "Innovatron",
	0x22: "Austria Card",
	0x23: "Carta+",
	0x24: "Imprimerie Nationale",
	0x25: "HID Global",
	0x26: "Card Project",
	0x27: "PosteMobile",
	0x28: "HB Technologies",
	0x29: "HID Global (Arjo Systems)",
	0x2A: "ANY Security Printing",
	0x2B: "SELP",
	0x2C: "FutureCard",
	0x2D: "iQuantics",
	0x2E: "Calypso Networks Association",
	0x2F: "Aruba PEC",
	0x30: "Identiv",
	0x31: "Qilium",
	0x32: "LS Card",
	0x33: "Arti Grafiche Julia",
	0x34: "Digimobee",
	0x35: "Infineon",
	0x36: "be ys Pay",
	0x37: "Snowball Tech",
	0x38: "MK Smart",
	0x39: "SIBS",
	0x3A: "TGS Card",
	0x3B: "Wizway Solutions",
	0x3D: "Confidex/Beontag",
};

/*
Returns the Platform name for the given byte value, or null when no
entry matches.
*/
export function getPlatformName(
	inCode:	number		// Platform byte value (0..255)
): string | null
{
	return kPlatformTable[inCode & 0xFF] ?? null;
}

/*
Returns the Manufacturer / Software Issuer name for the given byte
value, or null when no entry matches.
*/
export function getSoftwareIssuerName(
	inCode:	number		// Software Issuer byte value (0..255)
): string | null
{
	return kIssuerTable[inCode & 0xFF] ?? null;
}

/*
Returns the Application Type descriptor for the given byte value.
The Application Type table is range-based (Calypso Rev 1/2 split,
Calypso Prime Rev 3 range, plus singleton SAM/HSM/HAM/Light/Basic
codes), so this dispatcher is the cleanest form.
Returns null for unallocated values.
*/
export function getApplicationTypeName(
	inCode:	number		// Application Type byte value (0..255)
): string | null
{
	const vCode	= inCode & 0xFF;	// Normalised byte value

	if (vCode === 0x01 || vCode === 0x03)								{ return "Calypso Rev 1 or 2"; }
	if (vCode >= 0x06 && vCode <= 0x1F)									{ return "Calypso Rev 2"; }
	if (vCode >= 0x20 && vCode <= 0x3F)									{ return "Calypso Prime Rev 3"; }
	if (vCode >= 0x40 && vCode <= 0x7F)									{ return "Calypso Prime Rev 3 (reserved)"; }
	if (vCode === 0x80)													{ return "Calypso SAM"; }
	if (vCode === 0x82)													{ return "Calypso HAM"; }
	if (vCode === 0x88)													{ return "Calypso HSM"; }
	if (vCode === 0x90)													{ return "Calypso Light"; }
	if (vCode >= 0x91 && vCode <= 0x97)									{ return "Calypso Light (reserved)"; }
	if (vCode === 0x98)													{ return "Calypso Basic"; }
	if (vCode >= 0x99 && vCode <= 0x9F)									{ return "Calypso Basic (reserved)"; }
	if (vCode === 0xFF)													{ return "Not a Calypso application"; }

	return null;
}

/*
Resolves the display value for one DecodedField of the SELECT
section. For Platform / ApplicationType / SoftwareIssuer the lookup
tables override whatever the server-supplied `value` carries, so
already-stored cards whose Android-side encoder predates the lookup
helper still render with names. For other fields (and unrecognised
codes) the server `value` is returned untouched.
*/
export function describeSelectField(
	inName:		string,		// DecodedField.name (e.g. "Platform")
	inRaw:		string,		// DecodedField.raw (hex byte, e.g. "08")
	inValue:	string		// DecodedField.value as supplied by the server
): string
{
	const vCode	= parseHexByte(inRaw);	// Numeric code, or null when raw isn't a single byte

	if (vCode === null)
	{
		return inValue;
	}

	const vHex	= `${vCode.toString(16).toUpperCase().padStart(2, "0")}h`;	// "08h" form per the doc

	let vName: string | null = null;	// Resolved descriptor

	if (inName === "Platform")				{ vName = getPlatformName(vCode); }
	else if (inName === "SoftwareIssuer")	{ vName = getSoftwareIssuerName(vCode); }
	else if (inName === "ApplicationType")	{ vName = getApplicationTypeName(vCode); }
	else									{ return inValue; }

	return vName !== null ? `${vName} (${vHex})` : vHex;
}

/*
Parses a "08" / "0x08" / "08h" hex byte to a number (0..255). Returns
null when the input doesn't decode to a single byte.
*/
function parseHexByte(
	inRaw:	string		// Hex token from DecodedField.raw
): number | null
{
	const vTrimmed	= inRaw.trim()
		.replace(/^0x/i, "")
		.replace(/h$/i, "");

	if (!/^[0-9A-Fa-f]{1,2}$/.test(vTrimmed))
	{
		return null;
	}

	return Number.parseInt(vTrimmed, 16);
}
